import { useRef, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import {
  Box,
  Button,
  Collapse,
  Divider,
  Paper,
  Typography,
} from '@mui/material';
import { Sparkles } from 'lucide-react';

import { gql } from '@graphql/__generated__';
import { compactFeedActionButtonSx } from '@modules/_shared/components/feed-v2/constants/styles';
import { MarkdownRenderer } from '@modules/_shared/components/MardownRenderer';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { ANALYTICS_EVENTS, useAnalytics } from '@shared/analytics';
import { useLocale } from '@shared/hooks/locale';

const GENERATE_LEAD_SUMMARY = gql(/* GraphQL */ `
  mutation GenerateLeadSummary($lead_id: uuid!) {
    generateLeadSummary(lead_id: $lead_id) {
      summary
    }
  }
`);

type LeadSummaryProps = {
  leadId: string;
};

export const LeadSummary = ({ leadId }: LeadSummaryProps) => {
  const { t } = useLocale();
  const { track } = useAnalytics();
  const [summary, setSummary] = useState<string | null>(null);
  const [error, setError] = useState<ErrorLike | null>(null);
  const lastRequestHadSummaryRef = useRef(false);

  const [generateSummary, { loading }] = useMutation(GENERATE_LEAD_SUMMARY, {
    variables: { lead_id: leadId },
    onCompleted: data => {
      if (data.generateLeadSummary == null) {
        return;
      }

      track(ANALYTICS_EVENTS.LEAD_AI_SUMMARY_GENERATED, {
        lead_id: leadId,
        has_existing_summary: lastRequestHadSummaryRef.current,
        is_regeneration: lastRequestHadSummaryRef.current,
      });
      setSummary(data.generateLeadSummary.summary);
    },
    onError: setError,
  });

  const handleGenerateSummary = () => {
    const hasExistingSummary = summary != null;

    lastRequestHadSummaryRef.current = hasExistingSummary;
    track(ANALYTICS_EVENTS.AI_GENERATE_CLICKED, {
      source: 'lead_summary',
      lead_id: leadId,
      has_existing_summary: hasExistingSummary,
      is_regeneration: hasExistingSummary,
    });

    generateSummary();
  };

  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 1,
          pl: 2,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 500,
          }}
        >
          {t('AI Summary')}
        </Typography>
        <Button
          variant="contained"
          size="small"
          loadingPosition="start"
          loading={loading}
          startIcon={<Sparkles size={16} />}
          onClick={handleGenerateSummary}
          sx={compactFeedActionButtonSx}
        >
          {summary != null ? t('Regenerate') : t('Summarize')}
        </Button>
      </Box>
      <Collapse in={summary != null}>
        <Divider />
        {summary != null && (
          <MarkdownRenderer
            sx={{
              px: 2,
              py: 1,
              fontSize: 'body2.fontSize',
              color: 'text.secondary',
              '& p': { my: 1.5 },
              '& strong': { color: 'text.primary' },
            }}
          >
            {summary}
          </MarkdownRenderer>
        )}
      </Collapse>
      <MutationErrorSnackbar error={error} onClose={() => setError(null)} />
    </Paper>
  );
};
